<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                :pages="[
                    {
                        name: $tc('messages.family_interviews', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.index" method="GET" @submit.prevent="filter">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-12">
                <div class="sm:col-span-2">
                    <div class="">
                        <FormLabel :value="$tc('messages.groups', 1)" for="group_id" />
                        <FormSelect
                            id="group_id"
                            v-model="form.group_id"
                            :options="options.groups"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.group_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <div class="">
                        <FormLabel :value="$tc('messages.students', 1)" for="student_id" />
                        <FormSelect
                            :null-option-text="
                                form.group_id
                                    ? $t('messages.select')
                                    : $t('messages.select_X', { X: $tc('messages.groups', 1) })
                            "
                            id="student_id"
                            v-model="form.student_id"
                            :options="selects.students"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.student_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-3" v-show="options.school_years.length > 1">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.school_year')" for="school_year_id" />
                        <FormSelect
                            required
                            id="school_year_id"
                            v-model="form.school_year_id"
                            :options="options.school_years"
                            class="mt-1 w-full"
                            :null-option="false"
                        />
                        <FormError :value="form.errors.school_year_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="mt-1">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>
            </div>
        </form>

        <div class="w-full flex space-x-4 justify-end">
            <AppButton :href="meta.links.student_create" element="inertia-link" v-if="meta.links.student_create">
                {{ $t('messages.create') }}
            </AppButton>
            <AppButton :href="meta.links.meeting_turn_index" element="inertia-link">
                {{ $tc('messages.meeting_turns', 2) }}
            </AppButton>
        </div>

        <div class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.student') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.group') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.date') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.channels', 1) }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    <span class="sr-only">{{ $t('messages.actions') }}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                v-for="(familyInterview, index) in familyInterviews.data"
                                :key="familyInterview.id"
                            >
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <InertiaLink :href="familyInterview.student.links.show" class="text-indigo-500">
                                        {{ familyInterview.student.full_name }}
                                    </InertiaLink>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ familyInterview.group_name }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ familyInterview.date }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ familyInterview.type }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500 text-right flex space-x-1 justify-end"
                                >
                                    <InertiaSquareLinkButton action="show" :href="familyInterview.links.show" />
                                    <a
                                        :href="familyInterview.links.print"
                                        target="_blank"
                                        class="inline-flex items-center px-1.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                    >
                                        <svg viewBox="0 0 20 20" fill="currentColor" class="print w-4 h-4">
                                            <path
                                                fill-rule="evenodd"
                                                d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a2 2 0 002 2h6a2 2 0 002-2v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7a2 2 0 00-2 2zm8 0H7v3h6V4zm0 8H7v4h6v-4z"
                                                clip-rule="evenodd"
                                            ></path></svg
                                    ></a>
                                    <InertiaSquareLinkButton
                                        action="edit"
                                        :href="familyInterview.links.edit"
                                        v-if="familyInterview.can.edit"
                                    />
                                    <InertiaSquareDeleteButton
                                        :href="familyInterview.links.delete"
                                        v-if="familyInterview.can.delete"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <div>
            <div v-if="chartDataOptions" class="mt-5">
                <apexchart
                    :type="chartDataOptions.chart.type"
                    height="350"
                    :series="chartDataOptions.series"
                    :options="chartDataOptions"
                >
                </apexchart>
            </div>
        </div>

        <template v-slot:pagination>
            <div class="pt-5">
                <div class="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-5">
                    <AppCard v-for="day in countsPerDay" :key="day.url">
                        <div class="px-3 py-3 sm:p-6">
                            <dl>
                                <dt class="text-sm leading-5 font-medium text-gray-500 truncate">
                                    <InertiaLink :href="day.url">
                                        {{ day.text }}
                                    </InertiaLink>
                                </dt>
                                <dd class="mt-1 text-3xl leading-9 font-semibold text-gray-900">
                                    {{ day.count }}
                                </dd>
                            </dl>
                        </div>
                    </AppCard>
                </div>
            </div>

            <InertiaPagination :paginator="familyInterviews" />
        </template>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import InertiaPagination from '@/Shared/InertiaPagination.vue';
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';
import InertiaSquareDeleteButton from '@/components/InertiaSquareDeleteButton.vue';
import AppButton from '@/components/AppButton.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import axios from 'axios';
import { Link as InertiaLink } from '@inertiajs/vue3';
import AppCard from '@/Shared/AppCard.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        PanelPage,
        InertiaPagination,
        InertiaSquareLinkButton,
        InertiaSquareDeleteButton,
        AppButton,
        FormLabel,
        FormSelect,
        FormError,
        AppCard,
        InertiaLink,
    },
    layout: Layout,
    name: 'Index',
    props: ['meta', 'familyInterviews', 'filters', 'countsPerDay', 'chartDataOptions', 'options'],
    data() {
        return {
            form: this.$inertia.form({
                search: this.filters.search,
                group_id: this.filters.group_id,
                student_id: this.filters.student_id,
                school_year_id: this.filters.school_year_id,
            }),
            selects: {
                students: this.options.students,
            },
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
        fetchStudents() {
            axios.get(this.route('select.group_students.show', { group: this.form.group_id })).then((response) => {
                this.form.student_id = null;
                this.selects.students = response.data.items;
            });
        },
    },
    watch: {
        'form.group_id': {
            handler(newVal, oldVal) {
                if (newVal !== oldVal) {
                    if (newVal) {
                        this.fetchStudents();
                    } else {
                        this.form.student_id = null;
                        this.selects.students = [];
                    }
                }
            },
            deep: true,
        },
    },
};
</script>
